// COLOR MATH for the PaintPicker standard (2026-07-20).
//
// One import site for every conversion the house pickers need. The CMYK/RGB
// halves LIVE in ribbon/core/cmyk.mjs (re-exported here, not copied): that
// module documents the R4 print-honest-color contract — R4 replaces ITS
// bodies and every picker inherits the fix through this façade. HSV is new
// here: it exists for the in-house saturation/value field + hue bar (the
// custom picker PaintPicker ships instead of the bare <input type=color>
// the owner called primitive).
//
// Shapes: rgb {r,g,b} 0–255 · cmyk {c,m,y,k} 0–100 · hsv {h 0–360, s 0–1,
// v 0–1} · hex '#rrggbb'.

export { hexToRgb, rgbToHex, rgbToCmyk, cmykToRgb } from '../ribbon/core/cmyk.mjs';
import { hexToRgb, rgbToHex } from '../ribbon/core/cmyk.mjs';

/** {r,g,b} 0–255 → {h,s,v}. Gray (s=0) reports h=0 — callers that need a
 * stable hue across gray keep their own hue state (SolidEditor does). */
export function rgbToHsv({ r, g, b } = {}) {
  const R = (Number.isFinite(r) ? r : 0) / 255;
  const G = (Number.isFinite(g) ? g : 0) / 255;
  const B = (Number.isFinite(b) ? b : 0) / 255;
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const d = max - min;
  let h = 0;
  if (d > 0) {
    if (max === R) h = 60 * (((G - B) / d) % 6);
    else if (max === G) h = 60 * ((B - R) / d + 2);
    else h = 60 * ((R - G) / d + 4);
  }
  if (h < 0) h += 360;
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

/** {h 0–360, s 0–1, v 0–1} → {r,g,b} 0–255. */
export function hsvToRgb({ h, s, v } = {}) {
  const H = (((Number.isFinite(h) ? h : 0) % 360) + 360) % 360;
  const S = Math.min(1, Math.max(0, Number.isFinite(s) ? s : 0));
  const V = Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
  const c = V * S;
  const x = c * (1 - Math.abs(((H / 60) % 2) - 1));
  const m = V - c;
  const [R, G, B] = H < 60 ? [c, x, 0]
    : H < 120 ? [x, c, 0]
      : H < 180 ? [0, c, x]
        : H < 240 ? [0, x, c]
          : H < 300 ? [x, 0, c]
            : [c, 0, x];
  return {
    r: Math.round((R + m) * 255),
    g: Math.round((G + m) * 255),
    b: Math.round((B + m) * 255),
  };
}

export const hexToHsv = (hex) => rgbToHsv(hexToRgb(hex));
export const hsvToHex = (hsv) => rgbToHex(hsvToRgb(hsv));

/** Loose hex parse for typed input: '1a2b3c', '#1A2B3C', 'abc' → '#rrggbb';
 * 4/8-digit forms keep their alpha ('#rrggbbaa'); anything else → null. */
export function parseHexInput(raw) {
  if (typeof raw !== 'string') return null;
  let h = raw.trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{3,4}$/.test(h)) h = h.split('').map((c) => c + c).join('');
  return /^[0-9a-f]{6}$/.test(h) || /^[0-9a-f]{8}$/.test(h) ? `#${h}` : null;
}

// ---------------------------------------------------------------------------
// ALPHA-AWARE color strings (PaintPicker v2, 2026-07-20). The paint-opacity
// law: solids stay PLAIN CSS STRINGS — '#rrggbb' while opaque, 'rgba(r,g,b,a)'
// once alpha < 1 (rgba over 8-digit hex on purpose: every engine path this
// app has — fabric fills/strokes, canvas2d recipes, SVG export — parses rgba;
// hex8 support is spottier in older seams). Parsers below ACCEPT all of
// '#rgb', '#rgba', '#rrggbb', '#rrggbbaa', 'rgb()', 'rgba()'.

/** Any supported color string → {r,g,b,a} (a 0..1). Unparseable → null. */
export function parseColor(raw) {
  if (typeof raw !== 'string') return null;
  const s = raw.trim().toLowerCase();
  if (s.startsWith('#')) {
    let h = s.slice(1);
    if (/^[0-9a-f]{3,4}$/.test(h)) h = h.split('').map((c) => c + c).join('');
    if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/.test(h)) return null;
    const n = parseInt(h.slice(0, 6), 16);
    const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a };
  }
  const m = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (m) {
    const clamp255 = (v) => Math.max(0, Math.min(255, Math.round(parseFloat(v))));
    return {
      r: clamp255(m[1]),
      g: clamp255(m[2]),
      b: clamp255(m[3]),
      a: m[4] === undefined ? 1 : Math.max(0, Math.min(1, parseFloat(m[4]))),
    };
  }
  return null;
}

/** {r,g,b,a} → the emission-law string: '#rrggbb' opaque, 'rgba()' otherwise. */
export function formatColor({ r, g, b, a = 1 } = {}) {
  const A = Math.max(0, Math.min(1, Number.isFinite(a) ? a : 1));
  if (A >= 0.999) return rgbToHex({ r, g, b });
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${Math.round(A * 100) / 100})`;
}

/** Any color string → opaque '#rrggbb' (alpha STRIPPED — the seam for
 * engines that cannot carry alpha: three.js face colors, id-encoded params,
 * emboss's integer hex math). Unparseable → fallback. */
export function normalizeOpaqueHex(raw, fallback = '#000000') {
  const c = parseColor(raw);
  return c ? rgbToHex(c) : fallback;
}

/** Alpha 0..1 of any color string (1 when opaque or unparseable). */
export function alphaOf(raw) {
  return parseColor(raw)?.a ?? 1;
}

/** sRGB blend of two color strings at t (0 = a, 1 = b), alpha included —
 * the midpoint-bake + inserted-stop interpolation math. */
export function mixColors(aRaw, bRaw, t = 0.5) {
  const a = parseColor(aRaw) ?? { r: 0, g: 0, b: 0, a: 1 };
  const b = parseColor(bRaw) ?? { r: 255, g: 255, b: 255, a: 1 };
  const T = Math.max(0, Math.min(1, t));
  return formatColor({
    r: a.r + (b.r - a.r) * T,
    g: a.g + (b.g - a.g) * T,
    b: a.b + (b.b - a.b) * T,
    a: a.a + (b.a - a.a) * T,
  });
}
